import { useEffect, useRef, useState } from 'react';
import { CropView } from 'react-native-image-crop-tools';
import { getAngle } from '@v2/components/imageHandling/helpers';
import { CropperTabletProps } from '@v2/components/imageHandling/components/Cropper/components/CropperTablet';
import { CropMode } from '@v2/components/imageHandling/components/Cropper/components/CropperTabletMobile';
import { rotateImageFN } from '@v2/components/imageHandling/components/Cropper/helpers';
import { ImageDocumentStoreItemWithAngle } from '@v2/components/imageHandling/types/types.d';

interface UseManageCropperMobileTabletParams extends Pick<CropperTabletProps, 'image' | 'onSave'> {}

export const useManageCropperMobileTablet = ({ image, onSave }: UseManageCropperMobileTabletParams) => {
  const cropRef = useRef<CropView | null>(null);

  const cropRotationAngle = image?.cropSettings?.find((cs) => cs.pageNumber === image.pageNumber)?.rotationAngle || 0;
  const customRotateAngle = image?.customRotate?.[image.pageNumber] || 0;

  const [angle, setAngle] = useState<number>(() => getAngle(image));
  const [showDiscardChangesPopup, setShowDiscardChangesPopup] = useState<boolean>(false);
  const [showChooseCropModeModalOpen, setShowChooseCropModeModalOpen] = useState<boolean>(false);
  const [pendingCropData, setPendingCropData] = useState<ImageDocumentStoreItemWithAngle | null>(null);
  const modeRef = useRef<CropMode>(CropMode.SAVE);
  const triggerDiscardChangesPopup = () => setShowDiscardChangesPopup(!showDiscardChangesPopup);

  useEffect(() => {
    setAngle(getAngle(image));
  }, [image?.isCropped, cropRotationAngle, customRotateAngle, image?.pageNumber]);

  const onRotateImage = () => {
    rotateImageFN({ cropRef, angle, setAngle });
  };

  const saveImage = () => cropRef.current?.saveImage(true, 100);

  const onCancelButtonPress = () => {
    modeRef.current = CropMode.CANCEL;
    saveImage();
  };

  const onSaveButtonPress = () => {
    modeRef.current = CropMode.SAVE;
    saveImage();
  };

  const processCropData = (cropData: ImageDocumentStoreItemWithAngle | null) => {
    if (cropData) {
      onSave(cropData);
    }
  };

  return {
    angle,
    showDiscardChangesPopup,
    modeRef,
    triggerDiscardChangesPopup,
    onRotateImage,
    onCancelButtonPress,
    onSaveButtonPress,
    cropRef,
    showChooseCropModeModalOpen,
    setShowChooseCropModeModalOpen,
    pendingCropData,
    setPendingCropData,
    processCropData,
  };
};
